import React, { useState, useEffect } from 'react';
import {
  Form,
  Switch,
  Typography,
  Space,
  message,
  Spin,
  Alert
} from 'antd';
import {
  MailOutlined,
  PhoneOutlined,
  BellOutlined
} from '@ant-design/icons';
import NotificationSettingsService from '../../services/NotificationSettingsService';

const { Title, Text } = Typography;

const BookingNotificationSettings = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userInfo, setUserInfo] = useState(null);

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      const response = await NotificationSettingsService.getMyPreferences();
      
      if (response.success) {
        const prefs = response.data.preferences?.bookingConfirmations || {
          enabled: false,
          email: true,
          sms: false
        };
        setUserInfo(response.data.userInfo);
        form.setFieldsValue({
          enabled: prefs.enabled,
          email: prefs.email,
          sms: prefs.sms
        });
      }
    } catch (error) {
      console.error('Error fetching notification preferences:', error);
      message.error('Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = async (field, value) => {
    try {
      setSaving(true);
      const currentValues = form.getFieldsValue();
      const updatedValues = { ...currentValues, [field]: value };
      form.setFieldsValue(updatedValues);
      
      const response = await NotificationSettingsService.updateMyPreferences({
        bookingConfirmations: {
          enabled: updatedValues.enabled,
          email: updatedValues.email,
          sms: updatedValues.sms
        }
      });

      if (response.success) {
        message.success('Preferences saved');
      } else {
        message.error(response.message || 'Failed to save preferences');
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      message.error('Failed to save notification preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>Loading notification preferences...</div>
      </div>
    );
  }

  const isEnabled = form.getFieldValue('enabled');

  return (
    <div>
      <Text type="secondary" style={{ display: 'block', marginBottom: '24px' }}>
        Get notified when someone books an appointment through the public booking page.
      </Text>

      {/* Main Toggle */}
      <div style={{ 
        padding: '20px',
        background: '#fafafa',
        borderRadius: '8px',
        marginBottom: '16px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <BellOutlined style={{ fontSize: '20px' }} />
            <div>
              <Text strong style={{ display: 'block' }}>Receive Booking Notifications</Text>
              <Text type="secondary" style={{ fontSize: '13px' }}>
                Get notified when new bookings are made
              </Text>
            </div>
          </div>
          <Switch 
            checked={isEnabled}
            onChange={(checked) => handleSettingChange('enabled', checked)}
            loading={saving}
          />
        </div>
      </div>

      {/* Notification Methods */}
      <div style={{ 
        marginLeft: '24px',
        opacity: isEnabled ? 1 : 0.5,
        transition: 'opacity 0.2s'
      }}>
        <Title level={5} style={{ marginBottom: '16px', marginTop: '24px' }}>
          Notification Methods
        </Title>

        <Form form={form} layout="vertical">
          <div style={{ 
            padding: '16px',
            background: '#fff',
            border: '1px solid #f0f0f0',
            borderRadius: '8px',
            marginBottom: '12px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Space>
                <MailOutlined />
                <div>
                  <Text>Email</Text>
                  {userInfo?.email && (
                    <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>
                      {userInfo.email}
                    </Text>
                  )}
                </div>
              </Space>
              <Switch 
                checked={form.getFieldValue('email')}
                onChange={(checked) => handleSettingChange('email', checked)}
                disabled={!isEnabled}
                loading={saving}
              />
            </div>
          </div>

          <div style={{ 
            padding: '16px',
            background: '#fafafa',
            border: '1px solid #f0f0f0',
            borderRadius: '8px',
            opacity: 0.6
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Space>
                <PhoneOutlined />
                <div>
                  <Text>SMS</Text>
                  <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>
                    Coming soon
                  </Text>
                </div>
              </Space>
              <Switch 
                checked={false}
                disabled={true}
                loading={saving}
              />
            </div>
          </div>
        </Form>

        {!userInfo?.phone && isEnabled && (
          <Alert
            message="Add a phone number to your profile to receive SMS notifications"
            type="info"
            showIcon
            style={{ marginTop: '16px', display: 'none' }}
          />
        )}
      </div>
    </div>
  );
};

export default BookingNotificationSettings;
